// Удалённый экран браузера агента: владелец сам входит в Instagram, TikTok и другие сайты.
// Картинка и мышь — noVNC через WebSocket api (/api/browser/vnc); текст для полей (пароль, код) — отдельным
// полем, чтобы с телефона не бороться с экранной клавиатурой. Агент этих паролей не видит.
import { Button, Chip, toast } from '@autoaff/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { ArrowLeft, CornerDownLeft, Keyboard, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { SiteChip, useBrowser } from './BrowserCard';
import { PageHead } from './common';
import './browser.css';

type Rfb = import('@novnc/novnc').default;
type Conn = 'connecting' | 'connected' | 'closed' | 'failed';

/** Символ → X11 keysym: Latin-1 — своим кодом, остальное — диапазон Unicode (0x01000000 + код). */
export function keysymOf(ch: string): number {
  const cp = ch.codePointAt(0) ?? 0;
  if (ch === '\n') return 0xff0d;
  if ((cp >= 0x20 && cp <= 0x7e) || (cp >= 0xa0 && cp <= 0xff)) return cp;
  return 0x01000000 + cp;
}

export function BrowserScreenPage() {
  const search = useSearch({ strict: false }) as { site?: string };
  const q = useBrowser(true);
  const qc = useQueryClient();
  const box = useRef<HTMLDivElement>(null);
  const rfb = useRef<Rfb | null>(null);
  const [conn, setConn] = useState<Conn>('connecting');
  const [attempt, setAttempt] = useState(0);
  const [text, setText] = useState('');
  const [failMsg, setFailMsg] = useState<string | null>(null);
  const opened = useRef<string | null>(null);

  const open = useCallback(
    async (site: string) => {
      try {
        await api('/api/browser/open', { site });
        void qc.invalidateQueries({ queryKey: ['browser'] });
      } catch (e) {
        toast((e as Error).message);
      }
    },
    [qc],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt — повторное подключение по кнопке
  useEffect(() => {
    let cancelled = false;
    let r: Rfb | null = null;
    setConn('connecting');
    setFailMsg(null);
    (async () => {
      try {
        const { password } = await api<{ password: string }>('/api/browser/screen');
        const { default: RFB } = await import('@novnc/novnc');
        if (cancelled || !box.current) return;
        const url = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/api/browser/vnc`;
        r = new RFB(box.current, url, { credentials: { password }, shared: true });
        r.scaleViewport = true;
        r.background = 'transparent';
        r.addEventListener('connect', () => setConn('connected'));
        r.addEventListener('disconnect', () => setConn((c) => (c === 'connecting' ? 'failed' : 'closed')));
        r.addEventListener('securityfailure', () => setConn('failed'));
        rfb.current = r;
      } catch (e) {
        if (!cancelled) {
          setConn('failed');
          setFailMsg((e as Error).message);
        }
      }
    })();
    return () => {
      cancelled = true;
      r?.disconnect();
      rfb.current = null;
    };
  }, [attempt]);

  // Пришли из карточки по «Войти» — открыть страницу входа, как только экран подключился
  useEffect(() => {
    if (conn === 'connected' && search.site && opened.current !== search.site) {
      opened.current = search.site;
      void open(search.site);
    }
  }, [conn, search.site, open]);

  const type = (s: string) => {
    const r = rfb.current;
    if (!r || conn !== 'connected') return;
    for (const ch of s) r.sendKey(keysymOf(ch), null);
    r.focus();
  };

  const chip =
    conn === 'connected' ? (
      <Chip tone="success" dot>
        Подключено
      </Chip>
    ) : conn === 'connecting' ? (
      <Chip dot>Подключаюсь…</Chip>
    ) : (
      <Chip tone="warning" dot>
        {conn === 'failed' ? 'Не подключилось' : 'Отключено'}
      </Chip>
    );

  return (
    <>
      <PageHead
        title="Удалённый экран"
        sub="Браузер агента на сервере. Войдите в запасные аккаунты — агент пользуется входом, но паролей не видит."
        actions={
          <Link to="/settings" hash="browser" className="btn btn-ghost">
            <ArrowLeft />К настройкам
          </Link>
        }
      />
      <div className="pc bw-page">
        <div className="bw-bar">
          {chip}
          {conn !== 'connected' && conn !== 'connecting' && (
            <Button size="sm" icon={<RefreshCw />} onClick={() => setAttempt((n) => n + 1)}>
              Подключиться снова
            </Button>
          )}
          <fieldset className="bw-open">
            <legend className="sr-only">Открыть страницу входа</legend>
            {(q.data?.sites ?? []).map((s) => (
              <button
                key={s.id}
                type="button"
                className="bw-site"
                disabled={conn !== 'connected'}
                onClick={() => void open(s.id)}
              >
                <b>{s.name}</b>
                <SiteChip s={s} />
              </button>
            ))}
          </fieldset>
        </div>

        <div className="bw-screen" data-state={conn}>
          <div ref={box} className="bw-canvas" />
          {conn !== 'connected' && (
            <p className="bw-overlay">
              {conn === 'connecting'
                ? 'Подключаюсь к браузеру агента…'
                : conn === 'closed'
                  ? 'Экран отключён.'
                  : `Не удалось подключиться к экрану${failMsg ? `: ${failMsg.charAt(0).toLowerCase()}${failMsg.slice(1).replace(/\.$/, '')}` : ''}. Браузер агента мог перезапускаться — подключитесь снова через минуту.`}
            </p>
          )}
        </div>

        <form
          className="bw-type"
          onSubmit={(e) => {
            e.preventDefault();
            type(text);
            setText('');
          }}
        >
          <label className="label" htmlFor="bw-text">
            Текст в поле на экране
          </label>
          <p>Нажмите на поле на экране (логин, пароль, код из СМС), потом введите текст здесь.</p>
          <div className="bw-type-row">
            <input
              id="bw-text"
              className="input"
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <Button type="submit" icon={<Keyboard />} disabled={!text || conn !== 'connected'}>
              Напечатать
            </Button>
            <Button icon={<CornerDownLeft />} disabled={conn !== 'connected'} onClick={() => type('\n')}>
              Enter
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}
